// MarketingConsultant.jsx — AI-powered marketing idea bank.
// The manager describes a goal/challenge → Claude generates 3-5 ideas with feasibility,
// success chance, budget, channels, and a first concrete action. Manager saves keepers
// to a numbered persistent list, can also write their own idea and have Claude evaluate it.
import React, { useState, useEffect, useCallback } from "react";
import { api, formatErr } from "./lib/api";
import { useAuth } from "./lib/auth";
import { confirmWithUndo, showToast } from "./lib/theme";

const QUICK_QUESTIONS = [
  "أفكار لزيادة عملاء يوم الأحد",
  "أفكار لاستعادة العملاء الذين تأخروا عن عادتهم",
  "أفكار لزيادة متوسط الفاتورة",
  "حملات سناب شات بميزانية أقل من 1000 ريال",
  "أفكار لجذب طلاب الجامعات",
  "أفكار لساعات النهار البطيئة (10ص-2م)",
];

const STATUS_LABELS = {
  new: { lbl: "🆕 جديدة", color: "var(--info)" },
  in_progress: { lbl: "⏳ قيد التنفيذ", color: "var(--warn)" },
  done: { lbl: "✅ منفّذة", color: "var(--success)" },
  archived: { lbl: "📦 مؤرشفة", color: "var(--muted)" },
};

const COST_COLOR = {
  منخفض: "var(--success)",
  متوسط: "var(--warn)",
  مرتفع: "var(--danger)",
};

function ScoreBar({ value, color, label }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--muted)", marginBottom: 2 }}>
        <span>{label}</span>
        <b style={{ color }}>{v}%</b>
      </div>
      <div style={{ height: 6, background: "var(--beige)", borderRadius: 3, overflow: "hidden" }}>
        <div style={{ width: `${v}%`, height: "100%", background: color, transition: "width .3s" }} />
      </div>
    </div>
  );
}

function IdeaCard({ idea, onSave, onEvaluate, saved = false, saving = false, number }) {
  const successColor = (idea.success_chance || 0) >= 60 ? "var(--success)" : (idea.success_chance || 0) >= 40 ? "var(--warn)" : "var(--danger)";
  const feasColor = (idea.feasibility || 0) >= 60 ? "var(--success)" : (idea.feasibility || 0) >= 40 ? "var(--warn)" : "var(--danger)";
  return (
    <div className="card" style={{ marginBottom: 12, borderInlineStart: `4px solid ${successColor}` }} data-testid={`idea-card-${number || idea.id || "new"}`}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 8 }}>
        <div style={{ flex: 1 }}>
          {number !== undefined && <span style={{ fontSize: 10, color: "var(--muted)", fontWeight: 700 }}>#{number}</span>}
          <h4 style={{ margin: "2px 0 4px", color: "var(--brown-deep)", fontSize: 15 }}>{idea.title}</h4>
          {idea.summary && <p style={{ margin: 0, fontSize: 12, lineHeight: 1.7, color: "var(--text)" }}>{idea.summary}</p>}
        </div>
        {!saved && onSave && (
          <button type="button" className="btn btn-success" onClick={() => onSave(idea)} disabled={saving} data-testid={`save-idea-${number || 0}`} style={{ fontSize: 11, padding: "4px 10px", whiteSpace: "nowrap" }}>
            {saving ? "..." : "💾 احفظ"}
          </button>
        )}
      </div>

      <div className="g2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 8 }}>
        <ScoreBar value={idea.success_chance} color={successColor} label="فرصة النجاح" />
        <ScoreBar value={idea.feasibility} color={feasColor} label="الجدوى التنفيذية" />
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, fontSize: 11, marginBottom: 6 }}>
        {idea.cost_level && (
          <span style={{ padding: "3px 8px", background: COST_COLOR[idea.cost_level] || "var(--muted)", color: "#fff", borderRadius: 4 }}>
            💰 تكلفة {idea.cost_level}
          </span>
        )}
        {idea.estimated_budget_sar !== null && idea.estimated_budget_sar !== undefined && (
          <span style={{ padding: "3px 8px", background: "var(--beige)", borderRadius: 4 }}>
            💵 ~{Math.round(Number(idea.estimated_budget_sar)).toLocaleString("en-US")} ريال
          </span>
        )}
        {idea.audience && (
          <span style={{ padding: "3px 8px", background: "var(--beige)", borderRadius: 4 }}>
            🎯 {idea.audience}
          </span>
        )}
        {Array.isArray(idea.channels) && idea.channels.map((ch) => (
          <span key={ch} style={{ padding: "3px 8px", background: "#e8f5e9", borderRadius: 4 }}>📡 {ch}</span>
        ))}
      </div>

      {idea.first_action && (
        <div style={{ padding: 8, background: "var(--beige)", borderRadius: 4, fontSize: 12, marginBottom: 6 }}>
          <b style={{ color: "var(--brown-deep)" }}>🚀 أول خطوة:</b> {idea.first_action}
        </div>
      )}
      {idea.expected_kpi && (
        <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 4 }}>
          📈 <b>المتوقع:</b> {idea.expected_kpi}
        </div>
      )}
      {idea.risks && (
        <div style={{ fontSize: 11, color: "var(--danger)" }}>
          ⚠ <b>المخاطرة:</b> {idea.risks}
        </div>
      )}
      {onEvaluate && (
        <div style={{ marginTop: 8, textAlign: "left" }}>
          <button type="button" className="btn btn-soft" onClick={() => onEvaluate(idea)} style={{ fontSize: 11 }}>
            🔍 أعد تقييمها
          </button>
        </div>
      )}
    </div>
  );
}

function SavedIdeaCard({ idea, onDelete, onStatusChange, canEdit }) {
  const successColor = (idea.success_chance || 0) >= 60 ? "var(--success)" : (idea.success_chance || 0) >= 40 ? "var(--warn)" : "var(--danger)";
  const status = STATUS_LABELS[idea.status] || STATUS_LABELS.new;
  return (
    <div className="card" style={{ marginBottom: 10, borderInlineStart: `4px solid ${status.color}` }} data-testid={`saved-idea-${idea.number}`}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 18, fontWeight: 800, color: "var(--brown-deep)" }}>#{idea.number}</span>
          <h4 style={{ margin: 0, fontSize: 14 }}>{idea.title}</h4>
        </div>
        <div style={{ display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap" }}>
          {canEdit ? (
            <select
              value={idea.status || "new"}
              onChange={(e) => onStatusChange(idea, e.target.value)}
              data-testid={`status-select-${idea.number}`}
              style={{ fontSize: 11, padding: "2px 6px" }}
            >
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v.lbl}</option>
              ))}
            </select>
          ) : (
            <span style={{ padding: "3px 8px", background: status.color, color: "#fff", borderRadius: 4, fontSize: 11 }}>{status.lbl}</span>
          )}
          {canEdit && (
            <button type="button" className="btn" onClick={() => onDelete(idea)} data-testid={`delete-idea-${idea.number}`} style={{ background: "var(--danger)", color: "#fff", fontSize: 10, padding: "3px 8px" }}>
              🗑
            </button>
          )}
        </div>
      </div>
      {idea.summary && <p style={{ margin: "4px 0 6px", fontSize: 12, lineHeight: 1.7 }}>{idea.summary}</p>}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, fontSize: 11 }}>
        {idea.success_chance !== undefined && idea.success_chance !== null && (
          <span style={{ padding: "3px 8px", background: successColor, color: "#fff", borderRadius: 4 }}>
            🎯 نجاح {idea.success_chance}%
          </span>
        )}
        {idea.feasibility !== undefined && idea.feasibility !== null && (
          <span style={{ padding: "3px 8px", background: "var(--beige)", borderRadius: 4 }}>
            ⚙ جدوى {idea.feasibility}%
          </span>
        )}
        {idea.cost_level && (
          <span style={{ padding: "3px 8px", background: COST_COLOR[idea.cost_level] || "var(--muted)", color: "#fff", borderRadius: 4 }}>
            💰 {idea.cost_level}
          </span>
        )}
        {idea.estimated_budget_sar && (
          <span style={{ padding: "3px 8px", background: "var(--beige)", borderRadius: 4 }}>
            💵 ~{Math.round(Number(idea.estimated_budget_sar)).toLocaleString("en-US")} ر
          </span>
        )}
      </div>
      {idea.first_action && (
        <div style={{ marginTop: 6, padding: 6, background: "var(--beige)", borderRadius: 4, fontSize: 12 }}>
          🚀 {idea.first_action}
        </div>
      )}
      <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 4 }}>
        أنشئت بواسطة {idea.created_by} · {(idea.created_at || "").slice(0, 10)}
      </div>
    </div>
  );
}

export default function MarketingConsultant() {
  const { user } = useAuth();
  const canEdit = user.role === "admin" || user.role === "marketing";

  // Brainstorm flow
  const [question, setQuestion] = useState("");
  const [ideas, setIdeas] = useState([]);
  const [genBusy, setGenBusy] = useState(false);
  const [genErr, setGenErr] = useState(null);
  const [savingIdx, setSavingIdx] = useState(null);

  // Manual idea evaluation flow
  const [manualText, setManualText] = useState("");
  const [evalResult, setEvalResult] = useState(null);
  const [evalBusy, setEvalBusy] = useState(false);
  const [evalErr, setEvalErr] = useState(null);

  // Saved list
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");

  const loadSaved = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/marketing/ideas");
      setSaved(data.items || []);
    } catch (e) {
      // non-fatal — keep list empty
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadSaved(); }, [loadSaved]);

  async function generate(q) {
    const qText = String(q ?? question ?? "").trim();
    if (!qText) { setGenErr("اكتب سؤالاً أولاً"); return; }
    setGenBusy(true); setGenErr(null); setIdeas([]);
    try {
      const { data } = await api.post("/ai/marketing-ideas/generate", { question: qText });
      setIdeas(data.ideas || []);
    } catch (e) {
      setGenErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setGenBusy(false); }
  }

  const saveIdea = async (idea, idx) => {
    if (!canEdit) return;
    setSavingIdx(idx);
    try {
      const payload = {
        title: idea.title,
        summary: idea.summary || "",
        source: "ai",
        success_chance: idea.success_chance ?? null,
        feasibility: idea.feasibility ?? null,
        cost_level: idea.cost_level || null,
        estimated_budget_sar: idea.estimated_budget_sar ?? null,
        audience: idea.audience || "",
        channels: Array.isArray(idea.channels) ? idea.channels : [],
        first_action: idea.first_action || "",
        expected_kpi: idea.expected_kpi || "",
        risks: idea.risks || "",
        status: "new",
        raw: idea,
      };
      await api.post("/marketing/ideas", payload);
      await loadSaved();
    } catch (e) {
      setGenErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setSavingIdx(null); }
  };

  const evaluate = async (text) => {
    const t = (text || manualText).trim();
    if (!t) { setEvalErr("اكتب فكرتك أولاً"); return; }
    setEvalBusy(true); setEvalErr(null); setEvalResult(null);
    try {
      const { data } = await api.post("/ai/marketing-ideas/evaluate", { idea: t });
      const ev = data.evaluation || {};
      setEvalResult({
        title: ev.title || t.slice(0, 60),
        summary: t,
        success_chance: ev.success_chance,
        feasibility: ev.feasibility,
        cost_level: ev.cost_level,
        estimated_budget_sar: ev.estimated_budget_sar,
        first_action: ev.first_action,
        verdict: ev.verdict,
        strengths: ev.strengths || [],
        weaknesses: ev.weaknesses || [],
        improvements: ev.improvements || [],
      });
    } catch (e) {
      setEvalErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setEvalBusy(false); }
  };

  const saveEvaluatedIdea = async () => {
    if (!evalResult || !canEdit) return;
    try {
      await api.post("/marketing/ideas", {
        title: evalResult.title,
        summary: evalResult.summary,
        source: "manual",
        success_chance: evalResult.success_chance,
        feasibility: evalResult.feasibility,
        cost_level: evalResult.cost_level,
        estimated_budget_sar: evalResult.estimated_budget_sar,
        first_action: evalResult.first_action || "",
        notes: `الحكم: ${evalResult.verdict || ""}`,
        status: "new",
        raw: evalResult,
      });
      setEvalResult(null);
      setManualText("");
      await loadSaved();
    } catch (e) {
      setEvalErr(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const deleteIdea = (idea) => {
    // Optimistic delete with 5-sec undo window
    setSaved((cur) => cur.filter((i) => i.id !== idea.id));
    confirmWithUndo({
      message: `🗑 سيتم حذف الفكرة #${idea.number} "${idea.title}"`,
      duration: 5000,
      onConfirm: async () => {
        try { await api.delete(`/marketing/ideas/${idea.id}`); }
        catch (e) { showToast({ kind: "danger", message: "تعذّر الحذف — أعد المحاولة" }); await loadSaved(); }
      },
      onUndo: async () => {
        await loadSaved();  // Restore from server
        showToast({ kind: "success", message: "تم التراجع" });
      },
    });
  };

  const changeStatus = async (idea, newStatus) => {
    await api.patch(`/marketing/ideas/${idea.id}`, { status: newStatus });
    await loadSaved();
  };

  const filteredSaved = filterStatus === "all" ? saved : saved.filter((i) => (i.status || "new") === filterStatus);

  return (
    <div data-testid="marketing-consultant-page" style={{ maxWidth: 1100, margin: "0 auto" }}>
      <div className="card" style={{ marginBottom: 14 }}>
        <h3 style={{ margin: "0 0 4px", color: "var(--brown-deep)" }}>💡 المستشار التسويقي — بنك الأفكار</h3>
        <p style={{ margin: 0, fontSize: 12, color: "var(--muted)", lineHeight: 1.8 }}>
          اسأل المستشار الذكي عن أفكار تسويقية لمقهى ثواني، قيّم أفكارك الخاصة، واحفظ الأفكار المعتمدة في قائمة مرقّمة.
        </p>
      </div>

      {/* Brainstorm */}
      <div className="card" style={{ marginBottom: 14 }}>
        <h4 style={{ margin: "0 0 8px", color: "var(--brown-deep)" }}>🤖 اطلب أفكاراً من الذكاء الاصطناعي</h4>
        <textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="مثال: أحتاج 4 أفكار حملات على إنستغرام لزيادة عملاء الإفطار في الأسبوع القادم..."
          data-testid="brainstorm-input"
          rows={3}
          style={{ width: "100%", fontSize: 13, padding: 8, borderRadius: 6, border: "1px solid var(--border)", marginBottom: 8 }}
        />
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
          {QUICK_QUESTIONS.map((q) => (
            <button
              key={q}
              type="button"
              className="btn btn-soft"
              onClick={() => { setQuestion(q); generate(q); }}
              data-testid={`quick-${q.slice(0, 15)}`}
              style={{ fontSize: 11, padding: "4px 10px" }}
            >
              {q}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => generate()}
          disabled={genBusy || !question.trim()}
          data-testid="brainstorm-btn"
        >
          {genBusy ? "⏳ جارٍ التوليد..." : "✨ ولّد أفكاراً"}
        </button>
        {genErr && <div className="al alr" style={{ marginTop: 8 }} data-testid="brainstorm-error">⚠ {genErr}</div>}

        {ideas.length > 0 && (
          <div style={{ marginTop: 12 }} data-testid="ideas-list">
            <h5 style={{ margin: "0 0 8px", color: "var(--brown-deep)" }}>🎯 {ideas.length} أفكار مُقترحة</h5>
            {ideas.map((idea, idx) => (
              <IdeaCard
                key={idx}
                idea={idea}
                number={idx + 1}
                onSave={canEdit ? (i) => saveIdea(i, idx) : null}
                saving={savingIdx === idx}
              />
            ))}
          </div>
        )}
      </div>

      {/* Evaluate manual idea */}
      <div className="card" style={{ marginBottom: 14 }}>
        <h4 style={{ margin: "0 0 8px", color: "var(--brown-deep)" }}>🔍 قيّم فكرتك الخاصة</h4>
        <textarea
          value={manualText}
          onChange={(e) => setManualText(e.target.value)}
          placeholder="اكتب فكرتك التسويقية هنا، والذكاء الاصطناعي يقيّمها لك (نقاط قوة/ضعف/تحسينات)..."
          data-testid="evaluate-input"
          rows={3}
          style={{ width: "100%", fontSize: 13, padding: 8, borderRadius: 6, border: "1px solid var(--border)", marginBottom: 8 }}
        />
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => evaluate()}
          disabled={evalBusy || !manualText.trim()}
          data-testid="evaluate-btn"
        >
          {evalBusy ? "⏳ جارٍ التقييم..." : "🔍 قيّم الفكرة"}
        </button>
        {evalErr && <div className="al alr" style={{ marginTop: 8 }} data-testid="evaluate-error">⚠ {evalErr}</div>}

        {evalResult && (
          <div style={{ marginTop: 12 }} data-testid="evaluation-result">
            <IdeaCard idea={evalResult} number="✎" />
            <div style={{ background: "var(--beige)", padding: 10, borderRadius: 6, marginTop: 8 }}>
              {evalResult.verdict && (
                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--brown-deep)", marginBottom: 8 }}>
                  ⚖ {evalResult.verdict}
                </div>
              )}
              {evalResult.strengths?.length > 0 && (
                <div style={{ marginBottom: 6 }}>
                  <b style={{ color: "var(--success)" }}>✅ نقاط القوة:</b>
                  <ul style={{ margin: "4px 0 0", paddingInlineStart: 18, fontSize: 12 }}>
                    {evalResult.strengths.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </div>
              )}
              {evalResult.weaknesses?.length > 0 && (
                <div style={{ marginBottom: 6 }}>
                  <b style={{ color: "var(--danger)" }}>⚠ نقاط الضعف:</b>
                  <ul style={{ margin: "4px 0 0", paddingInlineStart: 18, fontSize: 12 }}>
                    {evalResult.weaknesses.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </div>
              )}
              {evalResult.improvements?.length > 0 && (
                <div>
                  <b style={{ color: "var(--info)" }}>💡 اقتراحات التحسين:</b>
                  <ul style={{ margin: "4px 0 0", paddingInlineStart: 18, fontSize: 12 }}>
                    {evalResult.improvements.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </div>
              )}
            </div>
            {canEdit && (
              <button type="button" className="btn btn-success" onClick={saveEvaluatedIdea} data-testid="save-evaluated" style={{ marginTop: 8 }}>
                💾 احفظ هذه الفكرة في البنك
              </button>
            )}
          </div>
        )}
      </div>

      {/* Saved ideas bank */}
      <div className="card" data-testid="saved-ideas-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
          <h4 style={{ margin: 0, color: "var(--brown-deep)" }}>📚 بنك الأفكار المحفوظة ({fmAr(saved.length)})</h4>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <button type="button" className={`btn btn-soft ${filterStatus === "all" ? "active" : ""}`} onClick={() => setFilterStatus("all")} data-testid="filter-all" style={{ fontSize: 11, padding: "4px 10px" }}>الكل</button>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <button key={k} type="button" className={`btn btn-soft ${filterStatus === k ? "active" : ""}`} onClick={() => setFilterStatus(k)} data-testid={`filter-${k}`} style={{ fontSize: 11, padding: "4px 10px" }}>
                {v.lbl}
              </button>
            ))}
          </div>
        </div>
        {loading ? (
          <div style={{ padding: 20, textAlign: "center", color: "var(--muted)" }}>⏳ جارٍ التحميل...</div>
        ) : filteredSaved.length === 0 ? (
          <div style={{ padding: 20, textAlign: "center", color: "var(--muted)", fontSize: 12 }}>
            لا توجد أفكار محفوظة بعد. ولّد أفكاراً من الأعلى واحفظ المعجبة منها.
          </div>
        ) : (
          filteredSaved.map((idea) => (
            <SavedIdeaCard
              key={idea.id}
              idea={idea}
              canEdit={canEdit}
              onDelete={deleteIdea}
              onStatusChange={changeStatus}
            />
          ))
        )}
      </div>
    </div>
  );
}

function fmAr(n) { return Math.round(Number(n) || 0).toLocaleString("en-US"); }
